import type { ReactNode } from 'react';
import { Card } from '@/components/atoms/card';
import { cn } from '@/lib/utils';

interface FormSectionCardProps {
  children: ReactNode;
  className?: string;
}

export function FormSectionCard({ children, className }: FormSectionCardProps) {
  return (
    <Card
      className={cn('rounded-xl border bg-card px-2.5 py-3 shadow-sm sm:px-3 sm:py-4', className)}
    >
      {children}
    </Card>
  );
}

interface FormAccordionCardProps {
  header: ReactNode;
  open: boolean;
  children?: ReactNode;
  className?: string;
  bodyClassName?: string;
  variant?: 'default' | 'required' | 'optional';
  alwaysOpen?: boolean;
  /**
   * Stable key echoed as `data-section`, so a form that blocks its own Save can
   * scroll the offending section back into view.
   */
  sectionId?: string;
}

export function FormAccordionCard({
  header,
  open,
  children,
  className,
  bodyClassName,
  variant = 'default',
  alwaysOpen = false,
  sectionId,
}: FormAccordionCardProps) {
  const isRequired = variant === 'required';
  const isOptional = variant === 'optional';
  const renderedOpen = alwaysOpen || open;

  return (
    <div
      data-form-accordion-card
      data-section={sectionId}
      className={cn(
        'rounded-xl border bg-card shadow-sm',
        isRequired && 'border-border bg-background shadow-sm',
        isOptional && 'border-border/60 bg-transparent shadow-none',
        className,
      )}
    >
      <div
        className={cn(
          'rounded-t-xl bg-muted/60 px-3 py-2.5 sm:px-4 [&>:first-child]:mb-0',
          isRequired && 'bg-background',
          isOptional && 'bg-transparent px-3 py-2 sm:px-3',
        )}
      >
        {header}
      </div>
      {renderedOpen && children != null && (
        <div
          className={cn(
            'rounded-b-xl border-t px-3 py-3 sm:px-4 sm:py-4',
            isOptional && 'border-border/60 px-3 py-3 sm:px-3',
            bodyClassName,
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
